import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
    SafetyTrainingContextType,
    TrainingTopic,
    TrainingPlan,
    TrainingSchedule,
    TrainingEnrollment,
    TrainingCompletion,
    TrainingAffidavit,
    TrainingQuizResponse,
    ComplianceStatus,
    CategoryProgress,
    PlanCategory,
    CreateTrainingTopicInput,
    UpdateTrainingTopicInput,
    CreateTrainingPlanInput,
    UpdateTrainingPlanInput,
    CreateTrainingScheduleInput,
    CreateTrainingCompletionInput,
    CreateTrainingAffidavitInput,
} from "../types/trainingTypes";
import {
    listTrainingTopicsByCompany,
    listTrainingPlansByCompany,
    listTrainingSchedulesByPlan,
    listTrainingEnrollmentsByPlan,
    listTrainingCompletionsByPlan,
    createTrainingTopic as gqlCreateTopic,
    updateTrainingTopic as gqlUpdateTopic,
    deleteTrainingTopic as gqlDeleteTopic,
    createTrainingPlan as gqlCreatePlan,
    updateTrainingPlan as gqlUpdatePlan,
    deleteTrainingPlan as gqlDeletePlan,
    createTrainingSchedule as gqlCreateSchedule,
    deleteTrainingSchedule as gqlDeleteSchedule,
    createTrainingEnrollment as gqlCreateEnrollment,
    deleteTrainingEnrollment as gqlDeleteEnrollment,
    createTrainingCompletion as gqlCreateCompletion,
    createTrainingAffidavit as gqlCreateAffidavit,
    listTrainingQuizResponsesByCompany,
} from "../graphql/trainingQueries";
import { calculateComplianceStatus, calculateCategoryProgress, parseLocalDateStr } from "../utils/trainingUtils";
import { useUserContext } from "./UserContext";
import { iProvider } from "../types";

// ---------------------------------------------------------------------------
// Default context value
// ---------------------------------------------------------------------------

const defaultContext: SafetyTrainingContextType = {
    topics: [],
    plans: [],
    schedules: [],
    enrollments: [],
    completions: [],
    quizResponses: [],
    isLoading: false,
    selectedPlanId: null,
    setSelectedPlanId: () => { },
    getActivePlan: () => null,
    getTodaySchedule: () => null,
    getUserCompletions: () => [],
    getUserComplianceStatus: () => "on-track",
    getCategoryProgress: () => [],
    getEnrolledUsers: () => [],
    refreshData: async () => { },
    createTopic: async () => ({} as TrainingTopic),
    updateTopic: async () => ({} as TrainingTopic),
    deleteTopic: async () => { },
    createPlan: async () => ({} as TrainingPlan),
    updatePlan: async () => ({} as TrainingPlan),
    deletePlan: async () => { },
    createScheduleEntry: async () => ({} as TrainingSchedule),
    deleteScheduleEntry: async () => { },
    enrollUser: async () => ({} as TrainingEnrollment),
    unenrollUser: async () => { },
    recordCompletion: async () => ({} as TrainingCompletion),
    createAffidavit: async () => ({} as TrainingAffidavit),
};

export const SafetyTrainingContext = createContext<SafetyTrainingContextType>(defaultContext);

// eslint-disable-next-line react-refresh/only-export-components
export const useSafetyTrainingContext = () => useContext(SafetyTrainingContext);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const SafetyTrainingProvider = ({ children, companyId: companyIdProp }: iProvider & { companyId?: string }) => {
    const { companyUser, companyOwner } = useUserContext();

    const [topics, setTopics] = useState<TrainingTopic[]>([]);
    const [plans, setPlans] = useState<TrainingPlan[]>([]);
    const [schedules, setSchedules] = useState<TrainingSchedule[]>([]);
    const [enrollments, setEnrollments] = useState<TrainingEnrollment[]>([]);
    const [completions, setCompletions] = useState<TrainingCompletion[]>([]);
    const [quizResponses, setQuizResponses] = useState<TrainingQuizResponse[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

    // Allow an explicit companyId override (e.g. "library-template" for business admin template mode)
    const companyId = companyIdProp || (companyUser?.companyId as string) || companyOwner?.companyId;
    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------

    const refreshData = useCallback(async () => {
        if (!companyId) return;
        setIsLoading(true);
        try {
            // Fetch topics and plans for the company
            const [topicItems, planItems] = await Promise.all([
                listTrainingTopicsByCompany(companyId),
                listTrainingPlansByCompany(companyId),
            ]);

            // Gen2 stores style/governingStandard/status as plain strings; the client-side
            // TrainingTopic/TrainingPlan types narrow them to unions. Cast at the boundary
            // without changing the underlying values.
            const fetchedTopics: TrainingTopic[] = topicItems as TrainingTopic[];
            const fetchedPlans: TrainingPlan[] = planItems as TrainingPlan[];

            setTopics(fetchedTopics);
            setPlans(fetchedPlans);

            // Fetch schedules, enrollments, and completions for each plan
            const allSchedules: TrainingSchedule[] = [];
            const allEnrollments: TrainingEnrollment[] = [];
            const allCompletions: TrainingCompletion[] = [];

            await Promise.all(
                fetchedPlans.map(async (plan) => {
                    const [schedItems, enrollItems, compItems] = await Promise.all([
                        listTrainingSchedulesByPlan(plan.id),
                        listTrainingEnrollmentsByPlan(plan.id),
                        listTrainingCompletionsByPlan(plan.id),
                    ]);
                    allSchedules.push(...schedItems);
                    allEnrollments.push(...enrollItems);
                    allCompletions.push(...compItems);
                }),
            );

            setSchedules(allSchedules);
            setEnrollments(allEnrollments);
            setCompletions(allCompletions);

            // Fetch quiz responses for the company
            try {
                const quizItems = await listTrainingQuizResponsesByCompany(companyId);
                setQuizResponses(quizItems);
            } catch {
                // Quiz responses table may not exist yet for older deployments
                setQuizResponses([]);
            }
        } catch (error) {
            console.error("Failed to fetch training data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [companyId]);

    // Fetch on mount / when companyId changes
    useEffect(() => {
        if (companyId) {
            refreshData();
        } else {
            setTopics([]);
            setPlans([]);
            setSchedules([]);
            setEnrollments([]);
            setCompletions([]);
        }
    }, [companyId, refreshData]);

    // -----------------------------------------------------------------------
    // Computed helpers
    // -----------------------------------------------------------------------

    /** Returns the first plan with ACTIVE status, or null. */
    const getActivePlan = useCallback((): TrainingPlan | null => {
        return plans.find((p) => p.status === "ACTIVE") ?? null;
    }, [plans]);

    /**
     * Returns the schedule entry for today's date for the given plan, or null.
     * Validates: Requirements 8.4
     */
    const getTodaySchedule = useCallback(
        (planId: string): TrainingSchedule | null => {
            const now = new Date();
            const y = now.getFullYear();
            const m = String(now.getMonth() + 1).padStart(2, '0');
            const d = String(now.getDate()).padStart(2, '0');
            const todayStr = `${y}-${m}-${d}`;
            return (
                schedules.find((s) => {
                    if (s.planId !== planId) return false;
                    const schedDate = s.scheduledDate.split("T")[0];
                    return schedDate === todayStr;
                }) ?? null
            );
        },
        [schedules],
    );

    /**
     * Returns all completion records for a user on a specific plan.
     * Validates: Requirements 8.6
     */
    const getUserCompletions = useCallback(
        (userId: string, planId: string): TrainingCompletion[] => {
            return completions.filter(
                (c) => c.userId === userId && c.planId === planId,
            );
        },
        [completions],
    );

    /**
     * Computes the compliance status for a user on a specific plan.
     * Uses calculateComplianceStatus from trainingUtils.
     * Validates: Requirements 8.4, 22.4
     */
    const getUserComplianceStatus = useCallback(
        (userId: string, planId: string): ComplianceStatus => {
            const plan = plans.find((p) => p.id === planId);
            if (!plan) return "on-track";

            const enrollment = enrollments.find(
                (e) => e.userId === userId && e.planId === planId,
            );
            if (!enrollment) return "on-track";

            const userCompletions = completions.filter(
                (c) => c.userId === userId && c.planId === planId,
            );
            const totalMinutesViewed = userCompletions.reduce(
                (sum, c) => sum + c.timeValueMinutes,
                0,
            );

            const enrolledDate = parseLocalDateStr(enrollment.enrolledDate);
            const now = new Date();
            const daysElapsed = Math.max(
                0,
                Math.floor(
                    (now.getTime() - enrolledDate.getTime()) / (1000 * 60 * 60 * 24),
                ),
            );

            return calculateComplianceStatus({
                totalMinutesViewed,
                totalMinutesRequired: plan.minimumTimeRequired,
                daysElapsed,
                totalCycleDays: plan.cycleDuration,
            });
        },
        [plans, enrollments, completions],
    );

    /**
     * Computes per-category progress for a user on a specific plan.
     * Uses calculateCategoryProgress from trainingUtils.
     * Validates: Requirements 8.9, 22.3
     */
    const getCategoryProgress = useCallback(
        (userId: string, planId: string): CategoryProgress[] => {
            const plan = plans.find((p) => p.id === planId);
            if (!plan) return [];

            let categories: PlanCategory[] = [];
            try {
                categories = JSON.parse(plan.categories) as PlanCategory[];
            } catch {
                return [];
            }

            const userCompletions = completions.filter(
                (c) => c.userId === userId && c.planId === planId,
            );

            return calculateCategoryProgress(categories, userCompletions, topics);
        },
        [plans, completions, topics],
    );

    /**
     * Returns all enrollments for a given plan.
     * Validates: Requirements 11.7
     */
    const getEnrolledUsers = useCallback(
        (planId: string): TrainingEnrollment[] => {
            return enrollments.filter((e) => e.planId === planId);
        },
        [enrollments],
    );

    // -----------------------------------------------------------------------
    // Mutation helpers
    // -----------------------------------------------------------------------

    const createTopic = useCallback(
        async (input: CreateTrainingTopicInput): Promise<TrainingTopic> => {
            const result = await gqlCreateTopic(input as any);
            await refreshData();
            return result as TrainingTopic;
        },
        [refreshData],
    );

    const updateTopic = useCallback(
        async (input: UpdateTrainingTopicInput): Promise<TrainingTopic> => {
            const result = await gqlUpdateTopic(input as any);
            await refreshData();
            return result as TrainingTopic;
        },
        [refreshData],
    );

    const deleteTopic = useCallback(
        async (id: string): Promise<void> => {
            await gqlDeleteTopic(id);
            await refreshData();
        },
        [refreshData],
    );

    const createPlan = useCallback(
        async (input: CreateTrainingPlanInput): Promise<TrainingPlan> => {
            const result = await gqlCreatePlan(input as any);
            await refreshData();
            return result as TrainingPlan;
        },
        [refreshData],
    );

    const updatePlan = useCallback(
        async (input: UpdateTrainingPlanInput): Promise<TrainingPlan> => {
            const result = await gqlUpdatePlan(input as any);
            await refreshData();
            return result as TrainingPlan;
        },
        [refreshData],
    );

    const deletePlan = useCallback(
        async (id: string): Promise<void> => {
            // Delete all schedule entries for this plan first
            const planSchedules = schedules.filter(s => s.planId === id);
            for (const sched of planSchedules) {
                await gqlDeleteSchedule(sched.id);
            }
            // Delete the plan itself (topics are NOT deleted)
            await gqlDeletePlan(id);
            await refreshData();
        },
        [schedules, refreshData],
    );

    const createScheduleEntry = useCallback(
        async (input: CreateTrainingScheduleInput): Promise<TrainingSchedule> => {
            const result = await gqlCreateSchedule(input as any);
            await refreshData();
            return result as TrainingSchedule;
        },
        [refreshData],
    );

    const deleteScheduleEntry = useCallback(
        async (id: string): Promise<void> => {
            await gqlDeleteSchedule(id);
            await refreshData();
        },
        [refreshData],
    );

    const enrollUser = useCallback(
        async (userId: string, planId: string): Promise<TrainingEnrollment> => {
            const result = await gqlCreateEnrollment({
                companyId: companyId!,
                userId,
                planId,
                enrolledDate: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; })(),
                status: "ACTIVE",
            } as any);
            await refreshData();
            return result as TrainingEnrollment;
        },
        [companyId, refreshData],
    );

    const unenrollUser = useCallback(
        async (enrollmentId: string): Promise<void> => {
            await gqlDeleteEnrollment(enrollmentId);
            await refreshData();
        },
        [refreshData],
    );

    const recordCompletion = useCallback(
        async (input: CreateTrainingCompletionInput): Promise<TrainingCompletion> => {
            const result = await gqlCreateCompletion(input as any);
            await refreshData();
            return result as TrainingCompletion;
        },
        [refreshData],
    );

    const createAffidavit = useCallback(
        async (input: CreateTrainingAffidavitInput): Promise<TrainingAffidavit> => {
            const result = await gqlCreateAffidavit(input as any);
            await refreshData();
            return result as TrainingAffidavit;
        },
        [refreshData],
    );

    // -----------------------------------------------------------------------
    // Context value
    // -----------------------------------------------------------------------

    const value: SafetyTrainingContextType = {
        topics,
        plans,
        schedules,
        enrollments,
        completions,
        quizResponses,
        isLoading,
        selectedPlanId,
        setSelectedPlanId,
        getActivePlan,
        getTodaySchedule,
        getUserCompletions,
        getUserComplianceStatus,
        getCategoryProgress,
        getEnrolledUsers,
        refreshData,
        createTopic,
        updateTopic,
        deleteTopic,
        createPlan,
        updatePlan,
        deletePlan,
        createScheduleEntry,
        deleteScheduleEntry,
        enrollUser,
        unenrollUser,
        recordCompletion,
        createAffidavit,
    };

    return (
        <SafetyTrainingContext.Provider value={value}>
            {children}
        </SafetyTrainingContext.Provider>
    );
};
